import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink, faGear } from '@fortawesome/pro-regular-svg-icons';
import { CircularProgress } from '@mui/material';
import { AsyncRenderer } from '@client/new/components/AsyncRenderer/AsyncRenderer';
import { TitledPage } from '@client/new/components/Misc';
import { FeatureSettings } from '@client/components/FeatureSettings';
import { ConnectedAccountsSection } from '../creator/settings/account/ConnectedAccountsSection';
import { Accordion } from '@design-system/components/accordion/Accordion';
import { Alert } from '@design-system/components/alert/alert';
import { AccordionHeader } from './components/AccordionHeader';
import { CustomDomainCard } from './components/CustomDomainCard';
import { sectionConfigs } from './components/sectionConfigs';
import { ContactInfoSection } from './contact-info/ContactInfoSection';
import { SignInEmailSection } from './sign-in-email/SignInEmailSection';
import { useSettingsViewModel } from './useSettings.viewModel';

type SettingsPageProps = {
  params: {
    influencerId: string;
  };
};

export const SettingsPage = (props: SettingsPageProps) => {
  const influencerId = parseInt(props.params.influencerId);
  const viewModel = useSettingsViewModel({ influencerId });

  return (
    <AsyncRenderer
      data={viewModel}
      loadRender={
        <TitledPage title="Settings">
          <div className="flex justify-center items-center">
            <CircularProgress />
          </div>
        </TitledPage>
      }
      renderer={(data) => (
        <TitledPage title="Settings">
          <div className="flex flex-col gap-4 max-w-screen-md">
            {/* Sign-in email */}
            <SignInEmailSection
              signInEmail={data.influencer.signInEmail}
              onRefetch={data.refetch}
            />

            {/* Connected accounts */}
            <Accordion
              defaultOpen={true}
              renderHeader={() => (
                <AccordionHeader
                  icon={<FontAwesomeIcon icon={faLink} />}
                  {...sectionConfigs.connectedAccounts}
                />
              )}
            >
              <ConnectedAccountsSection
                socialAccounts={data.influencer.socialAccounts}
                onRefresh={() => data.refetch()}
              />
            </Accordion>

            {/* Creator contact info */}
            <ContactInfoSection
              influencerId={data.influencer.id}
              email={data.influencer.email}
              mobilePhoneNumber={data.influencer.mobilePhoneNumber}
            />

            {/* Custom domain — static card, no accordion */}
            <CustomDomainCard influencerId={data.influencer.id} />

            {/* Feature settings */}
            <Accordion
              defaultOpen={true}
              renderHeader={() => (
                <AccordionHeader
                  icon={<FontAwesomeIcon icon={faGear} />}
                  {...sectionConfigs.featureSettings}
                  description={
                    data.showFeatureSettings
                      ? sectionConfigs.featureSettings.description
                      : undefined
                  }
                />
              )}
            >
              {data.showFeatureSettings ? (
                <FeatureSettings influencer={data.influencer} />
              ) : (
                <Alert
                  variant="info"
                  details="You can manage feature settings through Offers. Pick which features each offer has access to on the Offers page."
                />
              )}
            </Accordion>
          </div>
        </TitledPage>
      )}
    />
  );
};
